import { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useParams, useLocation } from "react-router-dom";
import { Anchor, Breadcrumbs, Group, Loader, Stack, Text, Title } from "@mantine/core";
import { Alert, Badge, Button, ConfirmModal, EmptyState, SectionCard, spacePx } from "@xplatform/design-system";
import { IconCircleCheck, IconGitCompare, IconHistory, IconPlus, IconSitemap } from "@tabler/icons-react";
import { useFlowConfig } from "@hooks/flow";
import { docTypeApi } from "@src/services/doctypeApi";
import { useAdminPermissions } from "@hooks/useAdminPermissions";
import { useTranslation } from "@hooks/useTranslation";
import {
    CreateFlowConfigDialog,
    FlowStepList,
    FlowSummary,
    VersionHistoryDrawer,
    VersionSelector,
    versionKey,
} from "@src/features";
import type { DocumentTypeDetailViewModel, FlowConfigViewModel, FlowVersionSummaryViewModel } from "@models/doctype";
import "./DocTypeFlowPage.scss";

type VersionRef = Pick<FlowVersionSummaryViewModel, "flowBaseId" | "version">;
type CreateMode = "version" | "flow" | null;

export const DocTypeFlowPage = () => {
    const { docTypeId } = useParams<{ docTypeId: string }>();
    const location = useLocation();
    const { t } = useTranslation();
    const { canEditDoctype } = useAdminPermissions();
    // Passed by the list page, so the header has a name before the doctype loads.
    const stateCode = (location.state as { docTypeCode?: string } | null)?.docTypeCode;
    const [doctype, setDoctype] = useState<DocumentTypeDetailViewModel | null>(null);
    const {
        flowVersions,
        selectedFlowConfig,
        isLoading,
        error,
        fetchFlowVersions,
        fetchFlowDetail,
        activateFlow,
    } = useFlowConfig();

    const [selectedRef, setSelectedRef] = useState<VersionRef | null>(null);
    const [createMode, setCreateMode] = useState<CreateMode>(null);
    const [historyOpen, setHistoryOpen] = useState(false);
    const [compareTarget, setCompareTarget] = useState<FlowVersionSummaryViewModel | null>(null);
    const [confirmActivate, setConfirmActivate] = useState(false);
    const [notice, setNotice] = useState<string | null>(null);

    const docTypeCode = doctype?.docTypeCode ?? stateCode;

    // Only this doctype (GET /doctypes/{id}), not the whole list.
    useEffect(() => {
        if (!docTypeId) return;
        let alive = true;
        docTypeApi.getDoctypeDetail(docTypeId)
            .then((detail) => alive && setDoctype(detail))
            .catch(() => alive && setDoctype(null));
        return () => {
            alive = false;
        };
    }, [docTypeId]);

    useEffect(() => {
        if (docTypeId) fetchFlowVersions(docTypeId);
    }, [docTypeId, fetchFlowVersions]);

    const selectVersion = useCallback((ref: VersionRef) => {
        if (!docTypeId) return;
        setSelectedRef({ flowBaseId: ref.flowBaseId, version: ref.version });
        fetchFlowDetail(docTypeId, ref.flowBaseId, ref.version);
    }, [docTypeId, fetchFlowDetail]);

    // First load: open the live version, or the newest when none is live.
    useEffect(() => {
        if (selectedRef || flowVersions.length === 0) return;
        selectVersion(flowVersions.find((v) => v.isActive) ?? flowVersions[0]);
    }, [flowVersions, selectedRef, selectVersion]);

    const selected = useMemo(
        () => (selectedRef ? flowVersions.find((v) => versionKey(v) === versionKey(selectedRef)) ?? null : null),
        [flowVersions, selectedRef],
    );
    const live = flowVersions.find((v) => v.isActive) ?? null;
    const isLive = !!selected?.isActive;
    const nextVersion = selectedFlowConfig
        ? Math.max(0, ...flowVersions.filter((v) => v.flowBaseId === selectedFlowConfig.flowBaseId).map((v) => v.version)) + 1
        : 1;

    /** Every save creates a version: refresh, open it, and say so. */
    const handleSaved = async (config: FlowConfigViewModel) => {
        if (!docTypeId) return;
        setNotice(t("admin.flowConfigs.notices.savedAsVersion", { version: config.version }));
        await fetchFlowVersions(docTypeId);
        selectVersion(config);
    };

    const handleActivate = async () => {
        if (!docTypeId || !selectedRef) return;
        setConfirmActivate(false);
        const activated = await activateFlow(docTypeId, {
            flow_base_id: selectedRef.flowBaseId,
            version: selectedRef.version,
        });
        if (activated) {
            setNotice(t("admin.flowConfigs.notices.activated", { version: selectedRef.version }));
            selectVersion(selectedRef);
        }
    };

    const openCompare = (target: FlowVersionSummaryViewModel | null) => {
        setCompareTarget(target);
        setHistoryOpen(true);
    };

    if (!docTypeId) {
        return <Alert color="red">{t("admin.flowConfigs.errors.missingDocType")}</Alert>;
    }

    const doctypeLabel = doctype
        ? `${doctype.docTypeName}${doctype.docTypeCode ? ` (${doctype.docTypeCode})` : ""}`
        : docTypeCode ?? t("admin.documentTypes.title");

    return (
        <Stack gap="md" className="doctype-flow-page">
            <Group justify="space-between" align="flex-end" wrap="wrap" gap="md">
                <Stack gap={spacePx[1]}>
                    <Breadcrumbs separatorMargin={6}>
                        <Anchor component={Link} to="/admin/doctypes" size="sm">
                            {t("admin.documentTypes.title")}
                        </Anchor>
                        <Text size="sm" c="dimmed">{doctypeLabel}</Text>
                    </Breadcrumbs>
                    <Group gap="sm">
                        <Title order={2} size="h3">{t("admin.flowConfigs.title")}</Title>
                        {selected && (
                            <>
                                <Badge size="lg" variant="light" color="gray" tt="none">
                                    {t("admin.flowConfigs.versions.label", { version: selected.version })}
                                </Badge>
                                <Badge size="lg" color={isLive ? "green" : "gray"} variant={isLive ? "filled" : "light"}>
                                    {isLive ? t("admin.flowConfigs.status.live") : t("admin.flowConfigs.status.draft")}
                                </Badge>
                            </>
                        )}
                    </Group>
                </Stack>
                {flowVersions.length > 0 && (
                    <Group gap="sm">
                        <VersionSelector
                            versions={flowVersions}
                            selectedVersion={selected}
                            onVersionChange={selectVersion}
                            isLoading={isLoading}
                        />
                        <Button variant="default" leftSection={<IconHistory size={16} />} onClick={() => openCompare(null)}>
                            {t("admin.flowConfigs.history.title")}
                        </Button>
                        {canEditDoctype && (
                            <>
                                <Button variant="default" leftSection={<IconPlus size={16} />} onClick={() => setCreateMode("flow")}>
                                    {t("admin.flowConfigs.actions.newFlow")}
                                </Button>
                                <Button
                                    leftSection={<IconPlus size={16} />}
                                    onClick={() => setCreateMode("version")}
                                    disabled={!selectedFlowConfig}
                                >
                                    {t("admin.flowConfigs.actions.newVersion")}
                                </Button>
                            </>
                        )}
                    </Group>
                )}
            </Group>

            {error && <Alert color="red">{error}</Alert>}
            {notice && (
                <Alert color="green" withCloseButton onClose={() => setNotice(null)}>
                    {notice}
                </Alert>
            )}

            {selected && !isLive && (
                <Alert color="yellow" className="doctype-flow-page__draft">
                    <Group justify="space-between" wrap="wrap" gap="sm">
                        <Text size="sm">
                            {live
                                ? t("admin.flowConfigs.banner.draft", { version: selected.version, live: live.version })
                                : t("admin.flowConfigs.banner.noneLive", { version: selected.version })}
                        </Text>
                        <Group gap="xs">
                            {live && (
                                <Button size="xs" variant="default" leftSection={<IconGitCompare size={14} />} onClick={() => openCompare(selected)}>
                                    {t("admin.flowConfigs.banner.compare", { live: live.version })}
                                </Button>
                            )}
                            {canEditDoctype && (
                                <Button
                                    size="xs"
                                    color="green"
                                    leftSection={<IconCircleCheck size={14} />}
                                    onClick={() => setConfirmActivate(true)}
                                >
                                    {t("admin.flowConfigs.banner.activate", { version: selected.version })}
                                </Button>
                            )}
                        </Group>
                    </Group>
                </Alert>
            )}

            {isLoading && !selectedFlowConfig ? (
                <Group justify="center" py="xl"><Loader /></Group>
            ) : !selectedFlowConfig ? (
                <SectionCard>
                    <EmptyState
                        icon={<IconSitemap size={32} />}
                        title={t("admin.flowConfigs.empty.noFlowConfigs")}
                        description={t("admin.flowConfigs.empty.description")}
                        action={canEditDoctype ? (
                            <Button leftSection={<IconPlus size={16} />} onClick={() => setCreateMode("flow")}>
                                {t("admin.flowConfigs.actions.createFlowConfig")}
                            </Button>
                        ) : undefined}
                    />
                </SectionCard>
            ) : (
                <>
                    <FlowSummary flowConfig={selectedFlowConfig} docTypeCode={docTypeCode} />
                    <Text fw={600} size="lg" mt="xs">{t("admin.flowConfigs.labels.steps")}</Text>
                    <FlowStepList
                        flowConfig={selectedFlowConfig}
                        docTypeId={docTypeId}
                        nextVersion={nextVersion}
                        canEdit={canEditDoctype}
                        onSaved={handleSaved}
                    />
                </>
            )}

            <CreateFlowConfigDialog
                open={createMode !== null}
                onClose={() => setCreateMode(null)}
                onSuccess={handleSaved}
                docTypeId={docTypeId}
                existingConfig={createMode === "version" ? selectedFlowConfig : null}
            />

            <VersionHistoryDrawer
                opened={historyOpen}
                onClose={() => setHistoryOpen(false)}
                docTypeId={docTypeId}
                versions={flowVersions}
                selected={selected}
                onSelect={selectVersion}
                compareTarget={compareTarget}
            />

            <ConfirmModal
                opened={confirmActivate}
                onClose={() => setConfirmActivate(false)}
                onConfirm={handleActivate}
                title={t("admin.flowConfigs.activate.title", { version: selected?.version })}
                message={
                    live
                        ? t("admin.flowConfigs.activate.replace", { version: selected?.version, live: live.version, doctype: doctypeLabel })
                        : t("admin.flowConfigs.activate.first", { version: selected?.version, doctype: doctypeLabel })
                }
                confirmLabel={t("admin.flowConfigs.activate.confirm", { version: selected?.version })}
                cancelLabel={t("common.cancel")}
                loading={isLoading}
                portalProps={{ target: "#admin-portal-root" }}
            />
        </Stack>
    );
};
